import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
} from 'date-fns';

export default {
  methods: {
    messageStamp(time, dateFormat = 'h:mm a') {
      const unixTime = fromUnixTime(time);
      return format(unixTime, dateFormat);
    },
    messageTimestamp(time, dateFormat = 'MMM d, yyyy') {
      const messageTime = fromUnixTime(time);
      const now = new Date();
      const messageDate = format(messageTime, dateFormat);
      if (!isSameYear(messageTime, now)) {
        return format(messageTime, 'LLL d y, h:mm a');
      }
      return messageDate;
    },
    dynamicTime(time) {
      // Defensive timestamp handling: catch null, undefined, 0, or very old timestamps
      // Problem: When timestamp is null/undefined/0, fromUnixTime(0) converts it to Unix epoch (Jan 1, 1970)
      // This causes "56y" to display instead of "now" in the conversation list
      // Solution: Any timestamp before 2020 is treated as invalid and shows "a few seconds ago"
      // Rationale: No legitimate conversation in this system should be from before 2020
      const YEAR_2020_UNIX = 1577836800; // January 1, 2020 in Unix timestamp

      if (!time || time < YEAR_2020_UNIX) {
        return 'a few seconds ago';
      }

      const unixTime = fromUnixTime(time);
      const formattedTime = formatDistanceToNow(unixTime, { addSuffix: true });
      return formattedTime
        .replace(
          /^(?:about |over |almost |less than |more than |nearly |just |in )+/,
          ''
        )
        .replace(/^(?:a |an )(?=\d)/, '');
    },
    dateFormat(time, dateFormat = 'MMM d, yyyy') {
      const unixTime = fromUnixTime(time);
      return format(unixTime, dateFormat);
    },
    shortTimestamp(time) {
      // Handle edge cases first
      if (!time || typeof time !== 'string') {
        return 'now';
      }

      const convertToShortTime = time
        .replace(/about|over|almost|/g, '')
        // Handle very recent timestamps (fixes "am" bug where "a few seconds ago" becomes "am" after partial replacement)
        // Show "now" for anything under a minute - gives a buffer for timestamp parsing issues
        .replace('less than a minute ago', 'now')
        .replace('a few seconds ago', 'now')
        .replace('few seconds ago', 'now')
        .replace('a minute ago', 'now')
        // IMPORTANT: Handle full phrases with "a/an" prefix BEFORE partial replacements
        // This prevents remaining phrases from becoming "am", "an h", etc.
        .replace('an hour ago', '1h')
        .replace('a day ago', '1d')
        .replace('a month ago', '1mo')
        .replace('a year ago', '1y')
        // Now handle partial phrases (numeric like "5 minutes ago")
        .replace(' minute ago', 'm')
        .replace(' minutes ago', 'm')
        .replace(' hour ago', 'h')
        .replace(' hours ago', 'h')
        .replace(' day ago', 'd')
        .replace(' days ago', 'd')
        .replace(' month ago', 'mo')
        .replace(' months ago', 'mo')
        .replace(' year ago', 'y')
        .replace(' years ago', 'y');

      // Final safety check: if we have a suspiciously old timestamp (>10 years), show "now"
      // This catches the "56y" bug that slips through when timestamps aren't caught by dynamicTime
      const yearMatch = convertToShortTime.match(/(\d+)y$/);
      if (yearMatch && parseInt(yearMatch[1], 10) > 10) {
        return 'now';
      }

      return convertToShortTime;
    },
  },
};
